import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

interface UserDropdownProps {
  isOpen: boolean
  onClose: () => void
  userName: string
  userEmail: string
  onSettingsClick: () => void
  onLogoutClick: () => void
}

export function UserDropdown({
  isOpen,
  onClose,
  userName,
  userEmail,
  onSettingsClick,
  onLogoutClick,
}: UserDropdownProps) {
  const { t } = useTranslation()
  const dropdownRef = useRef<HTMLDivElement>(null)
  const firstItemRef = useRef<HTMLButtonElement>(null)

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose])

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  // Focus first item when opened
  useEffect(() => {
    if (isOpen && firstItemRef.current) {
      firstItemRef.current.focus()
    }
  }, [isOpen])

  if (!isOpen) return null

  const dropdownStyles: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '8px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
    minWidth: '240px',
    zIndex: 1000,
    overflow: 'hidden',
  }

  const headerStyles: React.CSSProperties = {
    padding: '1rem',
    borderBottom: '1px solid #e5e7eb',
  }

  const nameStyles: React.CSSProperties = {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1a1a1a',
    marginBottom: '2px',
  }

  const emailStyles: React.CSSProperties = {
    fontSize: '13px',
    color: '#6b7280',
    wordBreak: 'break-all',
  }

  const menuItemStyles: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    padding: '12px 16px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#374151',
    textAlign: 'left',
    minHeight: '44px',
    transition: 'background-color 0.15s ease',
  }

  const handleItemHover = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.backgroundColor = '#f3f4f6'
  }

  const handleItemLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.backgroundColor = 'transparent'
  }

  return (
    <div ref={dropdownRef} style={dropdownStyles} role="menu" aria-label="User menu">
      <div style={headerStyles}>
        <div style={nameStyles}>{userName || 'User'}</div>
        <div style={emailStyles}>{userEmail}</div>
      </div>

      <div style={{ padding: '8px 0' }}>
        <button
          ref={firstItemRef}
          style={menuItemStyles}
          onClick={() => {
            onSettingsClick()
            onClose()
          }}
          onMouseEnter={handleItemHover}
          onMouseLeave={handleItemLeave}
          role="menuitem"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
          {t('header.settings')}
        </button>

        <button
          style={menuItemStyles}
          onClick={() => {
            onLogoutClick()
            onClose()
          }}
          onMouseEnter={handleItemHover}
          onMouseLeave={handleItemLeave}
          role="menuitem"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <polyline points="16,17 21,12 16,7" />
            <line x1="21" y1="12" x2="9" y2="12" />
          </svg>
          {t('header.logout')}
        </button>
      </div>
    </div>
  )
}
